"use client";

import React from "react";

import Image from "next/image";
import Chip from "@/components/global/ui/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";
import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTACornerButton } from "@/shadcn/ui/corner-button";
import { motion } from "framer-motion";

import { PageMentoringV2Schema } from "@/sanity/pages/PageMentoringV2Template";

const fadeInUp = (delay?: number) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" as const },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay },
});

export default function WhySection({ data, className }: { data: PageMentoringV2Schema; className?: string }) {
  return (
    <section className={cn("relative overflow-clip py-[8rem] lg:py-[16rem]", className)}>
      <DecorationLine
        className="hidden top-0 lg:block"
        orientation="vertical"
        position="left"
      />
      <DecorationLine
        className="hidden top-0 lg:block"
        orientation="vertical"
        position="right"
      />

      <div className="container flex flex-col items-center">
        <div className="w-[min(186rem,100%)] flex flex-col lg:flex-row items-center lg:items-stretch gap-[4rem] lg:gap-[8rem]">
          {/* Left: Image */}
          {data?.whyImage && (
            <motion.div
              className="w-[min(86.625rem,100%)] shrink-0"
              {...fadeInUp()}>
              <Image
                className="object-cover w-full h-auto"
                src={data.whyImage}
                alt=""
                width={data.whyImage.width}
                height={data.whyImage.height}
                placeholder={data.whyImage.lqip ? "blur" : undefined}
                blurDataURL={data.whyImage.lqip}
              />
            </motion.div>
          )}

          {/* Right: Content */}
          <div className="w-[min(90.25rem,100%)] flex flex-col justify-between">
            <div>
              {data?.whyChip && (
                <motion.div {...fadeInUp(0.05)}>
                  <Chip
                    image={data.whyChip.icon}
                    text={data.whyChip.text}
                  />
                </motion.div>
              )}

              {data?.whyTitle && (
                <motion.div
                  {...fadeInUp(0.1)}
                  className="richtext richtext-start lg:prose-headings:text-[7rem] prose-headings:leading-[100%] prose-headings:font-ivy-headline-regular prose-headings:tracking-[0.05px] prose-headings:text-[5rem]">
                  <PortableText
                    value={data.whyTitle}
                    components={richTextComponent}
                  />
                </motion.div>
              )}

              {data?.whyText && (
                <motion.div
                  {...fadeInUp(0.15)}
                  className="richtext richtext-start mt-[2rem] prose-p:text-black/80">
                  <PortableText
                    value={data.whyText}
                    components={richTextComponent}
                  />
                </motion.div>
              )}

              {data?.whyBullets && data.whyBullets.length > 0 && (
                <motion.ul
                  {...fadeInUp(0.2)}
                  className="flex flex-col py-[1.5rem] mt-[3rem] gap-[1.5rem] bg-[#0A1A1D]/5 border-l-[#0A1A1D] border-l-[0.3rem] px-[2.5rem]">
                  {data.whyBullets.map((bullet, index) => (
                    <li
                      key={index}
                      className="flex items-start gap-[1rem] text-[2rem] text-black/80">
                      <span className="flex-shrink-0 w-[0.8rem] h-[0.8rem] bg-[#0A1A1D] mt-[0.8rem]" />
                      {bullet.text}
                    </li>
                  ))}
                </motion.ul>
              )}
            </div>

            <div>
              {data?.whyCallout && (
                <motion.div
                  {...fadeInUp(0.25)}
                  className="richtext richtext-start mt-[3rem] px-[2.5rem] py-[1.5rem] border-l-[0.3rem] border-l-[#B45757] bg-[#B45757]/5 pmt-[2rem pmt-[2rem]]]] prose-h4:text-[2.25rem] prose-h4:p-0 prose-h4:m-0 prose-h4:font-geist prose-p:m-0 prose-p:text-black/60 prose-p:mt-[1rem]">
                  <PortableText
                    value={data.whyCallout}
                    components={richTextComponent}
                  />
                </motion.div>
              )}

              {data?.whyCTA && (
                <motion.div
                  className="w-full sm:w-auto"
                  {...fadeInUp(0.3)}>
                  <CTACornerButton
                    className="mt-[4rem]"
                    data={data.whyCTA}
                  />
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
